import { Link } from "@tanstack/react-router";
import { cn } from "../lib/utils";
import { Pastille } from "./badges";
import type { Portefeuille, PrerequisOuvert, Relance } from "./contract";
import {
  ageCourt,
  ageEnClair,
  compter,
  estAncien,
  formatDate,
  nomCourt,
  relanceEnClair,
  SEUIL_ANCIEN_JOURS,
  typeEnClair,
} from "./format";
import { type Demandes, heureDeDemande } from "./portefeuille-demandes";
import { LienGuide, LienPari } from "./portefeuille-liens";
import { classerGestes, type GroupeGestes, gestesPartages } from "./portefeuille-tri";

/**
 * « Ce qui t'attend » : les gestes ouverts, groupes par pari et ranges par ce qu'ils
 * debloquent (issue #125).
 *
 * Ce que ce rail ne fait plus, et c'est le point de la refonte : derouler 32 lignes a plat
 * triees par anciennete. Un groupe replie tient en une ligne et porte son compte, l'age de
 * son geste le plus vieux et le premier d'entre eux ; la hauteur du rail suit desormais le
 * nombre de paris en cours, pas le nombre de gestes.
 *
 * Tous les groupes arrivent replies. Le sommaire dit assez pour decider ou aller, et un rail
 * ouvert en entier redonnerait le mur que la page avait.
 */

/** Ce qui a trop attendu s'ecrit en rouge : un geste vieux d'une semaine, une relance echue. */
const EN_RETARD = "font-medium text-destructive";

export function CeQuiTAttend({
  portefeuille,
  maintenant,
  demandes,
}: {
  portefeuille: Portefeuille;
  maintenant: number;
  /** Les demandes « C'est fait » encore en attente, par cible (`portefeuille-demandes.ts`). */
  demandes: Demandes;
}) {
  const { paris, prerequis, relances } = portefeuille;
  const familles = classerGestes(prerequis, paris, maintenant);
  const partages = gestesPartages(prerequis);
  // Le compteur de retard ne compte que ce qui rougit vraiment : un geste qui rouvrirait une
  // idee au repos n'attend personne, l'annoncer en rouge gonflerait le chiffre sans rien dire.
  const enRetard = familles
    .flatMap((famille) => famille.groupes)
    .filter((groupe) => groupe.urgent)
    .flatMap((groupe) => groupe.gestes)
    .filter((geste) => estAncien(geste.depuis, maintenant)).length;

  return (
    <aside aria-labelledby="ce-qui-tattend" className="flex min-w-0 flex-col gap-5">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-lg font-semibold" id="ce-qui-tattend">
          Ce qui t'attend
        </h2>
        <p className="text-sm text-muted-foreground">
          {/* « 0 geste » ne s'ecrit pas : quand il ne reste qu'une relance, le compte des
              gestes n'apprend rien, et la liste des relances est juste en dessous. */}
          {prerequis.length === 0
            ? relances.length === 0
              ? "Rien ne t'attend."
              : "Aucun geste ouvert."
            : compter(prerequis.length, "geste", "gestes")}
          {enRetard > 0 ? (
            <>
              {" · "}
              <span className={EN_RETARD}>{enRetard} de plus de sept jours</span>
            </>
          ) : null}
        </p>
      </div>

      {partages.map((partage) => (
        <section
          aria-label={`Geste partagé : ${partage.quoi}`}
          className="flex flex-col gap-1 rounded-lg border border-foreground/25 bg-card p-3"
          key={partage.guide}
        >
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Même geste sur {compter(partage.gestes.length, "pari", "paris")}
          </p>
          <p className="text-sm font-medium">{partage.quoi}</p>
          {/* Un guide par dossier : ce sont trois fichiers, donc trois liens. Les reunir
              sous une seule adresse ferait croire a une seule page. */}
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
            {partage.gestes.map((geste) => {
              const nom = geste.titre === null ? "ce pari" : nomCourt(geste.titre);
              return (
                <li key={geste.cible ?? `${geste.pari}-${geste.quoi}`}>
                  {/* Le texte visible est le nom du pari : sans plus, deux liens de la page
                      porteraient le meme nom pour deux destinations. */}
                  <LienGuide
                    className="text-muted-foreground"
                    geste={geste}
                    nomAccessible={`${partage.quoi}, pour ${nom}`}
                  >
                    {nom}
                  </LienGuide>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {familles.map((famille) => (
        <section aria-label={famille.titre} className="flex flex-col" key={famille.cle}>
          <p className="flex items-baseline justify-between gap-2 pb-1 text-xs uppercase tracking-wide text-muted-foreground">
            <span>{famille.titre}</span>
            <span className="tabular-nums">{famille.total}</span>
          </p>
          {famille.groupes.map((groupe) => (
            <Groupe demandes={demandes} groupe={groupe} key={groupe.cle} maintenant={maintenant} />
          ))}
        </section>
      ))}

      {relances.length > 0 ? (
        <section aria-label="Prochaines dates" className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Prochaines dates</p>
          <ul aria-label="Relances" className="flex flex-col gap-2 text-sm">
            {relances.map((relance) => (
              <li className="flex flex-wrap items-baseline gap-x-2" key={relance.pari}>
                <span className="font-medium tabular-nums">
                  {formatDate(relance.prochaineDate)}
                </span>
                <LienRelance relance={relance} />
                <span
                  className={cn(
                    "ml-auto whitespace-nowrap",
                    relance.jours < 0 ? EN_RETARD : "text-muted-foreground",
                  )}
                >
                  {relanceEnClair(relance.jours)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* L'API ne retient une relance qu'echue ou a moins de sept jours : une liste vide est
          une information, pas un oubli de la page. */}
      {prerequis.length > 0 && relances.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucune relance sous sept jours.</p>
      ) : null}
    </aside>
  );
}

/**
 * Un pari et ses gestes, replie. Le sommaire porte tout ce qui sert a decider d'ouvrir :
 * le nom du pari, le compte, l'age du plus vieux geste et le premier d'entre eux.
 */
function Groupe({
  groupe,
  maintenant,
  demandes,
}: {
  groupe: GroupeGestes;
  maintenant: number;
  demandes: Demandes;
}) {
  // Un groupe nait toujours d'un geste (`classerGestes`), mais le dire au verificateur de
  // types coute une ligne et evite un `!` qui masquerait un vrai trou un jour.
  const premier = groupe.gestes[0];
  if (premier === undefined) return null;
  // Juge sur `groupe.jours`, le chiffre que le sommaire affiche : les deux ne peuvent pas
  // se contredire.
  const rouge = groupe.urgent && groupe.jours !== null && groupe.jours >= SEUIL_ANCIEN_JOURS;

  return (
    // `aria-label` sur le `details` : son sommaire ne le nomme pas tout seul partout.
    <details aria-label={groupe.nom} className="group border-t border-border last:border-b">
      <summary className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 rounded py-2.5 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <span className="flex min-w-0 items-baseline gap-1.5 font-medium">
          <span
            aria-hidden="true"
            className="text-muted-foreground transition-transform group-open:rotate-90"
          >
            &rsaquo;
          </span>
          {/* Le nom du pari mene a sa fiche, comme le faisait chaque ligne de l'ancienne
              liste. `stopPropagation` garde le clic pour le lien : sans lui, l'activation
              atteindrait le `summary` et deplierait le groupe en meme temps. Un pari au
              repos n'a pas de carte a l'ecran, c'est donc le seul chemin qui lui reste. */}
          {groupe.pari === null || groupe.titre === null ? (
            <span className="min-w-0 truncate" title={groupe.titre ?? undefined}>
              {groupe.nom}
            </span>
          ) : (
            <LienPari
              className="min-w-0 truncate"
              court
              discret
              id={groupe.pari}
              onClick={(evenement) => evenement.stopPropagation()}
              titre={groupe.titre}
            />
          )}
        </span>
        <span className="whitespace-nowrap text-sm text-muted-foreground tabular-nums">
          {compter(groupe.gestes.length, "geste", "gestes")}
          {groupe.jours === null ? null : (
            <>
              {" · "}
              {/* `groupe.jours` fait foi, et non l'age du premier : les deux ne coincident
                  que tant que l'API rend les gestes du plus ancien au plus recent. */}
              <span className={rouge ? EN_RETARD : undefined}>{groupe.jours} j</span>
            </>
          )}
        </span>
        {/* L'apercu disparait des que le groupe s'ouvre : le premier geste est alors juste
            en dessous, et le lire deux fois ne dit rien de plus. Il est purement visuel :
            un lecteur d'ecran trouve le meme texte dans la liste, en lien cliquable. */}
        <span
          aria-hidden="true"
          className="col-span-2 truncate pl-4 text-sm text-muted-foreground group-open:hidden"
        >
          {premier.quoi}
        </span>
      </summary>
      <ul aria-label={`Gestes de ${groupe.nom}`} className="flex flex-col gap-2.5 pb-3 pl-4">
        {groupe.gestes.map((geste) => (
          <Geste
            demandeLe={geste.cible === null ? null : (demandes[geste.cible] ?? null)}
            geste={geste}
            key={geste.cible ?? `${geste.pari}-${geste.quoi}`}
            maintenant={maintenant}
            urgent={groupe.urgent}
          />
        ))}
      </ul>
    </details>
  );
}

function Geste({
  geste,
  maintenant,
  urgent,
  demandeLe,
}: {
  geste: PrerequisOuvert;
  maintenant: number;
  urgent: boolean;
  /** L'heure d'une demande « C'est fait » qui attend son commit, ou null. */
  demandeLe: string | null;
}) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 text-sm">
      <span className="min-w-0">
        <Pastille>{typeEnClair(geste.type)}</Pastille> <LienGuide geste={geste} />
        {/* Le geste reste dans la liste jusqu'au commit : la ligne dit qu'il est deja parti,
            pour qu'on ne le refasse pas. */}
        {demandeLe === null ? null : (
          <>
            {" "}
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              demande envoyée à {heureDeDemande(demandeLe)}
            </span>
          </>
        )}
      </span>
      <span
        className={cn(
          "whitespace-nowrap text-right tabular-nums",
          urgent && estAncien(geste.depuis, maintenant) ? EN_RETARD : "text-muted-foreground",
        )}
        title={ageEnClair(geste.depuis, maintenant)}
      >
        {ageCourt(geste.depuis, maintenant)}
      </span>
    </li>
  );
}

/**
 * La ligne d'une relance mene au guide de la relance quand elle porte une idee ; une
 * opportunite du scout n'a pas de dossier, donc pas de guide.
 */
function LienRelance({ relance }: { relance: Relance }) {
  const nom = nomCourt(relance.titre);
  if (!relance.pari.startsWith("idee:")) {
    return <LienPari court id={relance.pari} titre={relance.titre} />;
  }
  return (
    // Le nom du pari est deja le texte d'une carte plus haut : sans nom explicite, deux
    // liens de la page s'appelleraient pareil pour deux destinations.
    <Link
      aria-label={`Relance de ${nom}`}
      className="font-medium underline underline-offset-2"
      params={{ slug: relance.pari.slice("idee:".length) }}
      title={relance.titre}
      to="/portefeuille/idees/$slug/relance"
    >
      {nom}
    </Link>
  );
}
